import type { EnvironmentProviders } from '@angular/core';
import { isDevMode } from '@angular/core';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { getMe, setInterfaceLanguage } from './effects/user-settings.effect';
import {
  armRedirectAfterLogin,
  getUserProfile,
  logout,
  logoutCallback,
  redirectToHome,
  setStoreSession,
} from './effects/user.effects';
import {
  socialLoginByCode,
  startLoadRequiredData,
} from './effects/auth.effects';
import { organizationFeature } from './state/organization/organization.feature';
import {
  loadActiveOrganization,
  loadMyOrganizations$,
} from './effects/organization.effect';
import { userFeature } from './state/user/user.feature';
import { userSettingsFeature } from './state/user-settings/user-settings.feature';
import { connectivityFeature } from './state/connectivity/connectivity.feature';
import {
  inactivityTicks$,
  logoutCleanup$,
  registerUserActivity$,
} from './effects/connectivity.effects';
import { permissionsFeature } from './state/permissions/permissions.feature';
import { getPermissions$ } from './effects/permissions.effects';
import { routeFeature } from './state/route/route.feature';
import { routerEvents$ } from './effects/router.effects';
import { sharedFeature } from './state/shared/shared.feature';
import { showMessage$ } from './effects/shared.effects';
import { contactsFeature } from './state/contacts/contacts.feature';
import {
  createContact$,
  deleteContact$,
  loadContacts$,
  loadMemberStats$,
  loadMoreContacts$,
  importContacts$,
  reloadContacts$,
  resetContactsSearch$,
  searchContacts$,
  startImportContacts$,
  startCreateContact$,
  startDeleteContact$,
  startLoadingContacts$,
  startUpdateContact$,
  updateContact$,
} from './effects/contacts.effects';
import { contactGroupsFeature } from './state/contact-groups/contact-groups.feature';
import {
  createContactGroup$,
  deleteContactGroup$,
  loadContactGroupMembers$,
  loadContactGroups$,
  removeContactGroupMember$,
  startCreateContactGroup$,
  startDeleteContactGroup$,
  startLoadingContactGroupMembers$,
  startLoadingContactGroups$,
  startRemoveContactGroupMember$,
  startUpdateContactGroup$,
  startUpdateContactGroupMembers$,
  updateContactGroup$,
  updateContactGroupMembers$,
} from './effects/contact-groups.effects';
import { httpRequestFeature } from './state/http-request/http-request.feature';
import { systemConfigurationFeature } from './state/system-configuration/system-configuration.feature';
import {
  loadSystemConfiguration$,
  saveSystemConfiguration$,
} from './effects/system-configuration.effects';
import {
  createHttpRequest$,
  deleteHttpRequest$,
  loadHttpRequests$,
  setEnabled$,
  startCreateHttpRequest$,
  startDeleteHttpRequest$,
  startLoadingHttpRequests$,
  startSetEnabled$,
  startUpdateHttpRequest$,
  updateHttpRequest$,
} from './effects/http-request.effects';
import { orgUsersFeature } from './state/org-users/org-users.feature';
import {
  loadOrgUsers$,
  startLoadingOrgUsers$,
} from './effects/org-users.effects';
import { contactsActivityLogFeature } from './state/contacts-activity-log/contacts-activity-log.feature';
import {
  loadContactsActivityLog$,
  loadContactsActivityLogById$,
  startLoadingContactsActivityLog$,
} from './effects/contacts-activity-log.effects';
import { userActivityLogFeature } from './state/user-activity-log/user-activity-log.feature';
import {
  loadUserActivityLog$,
  startLoadingUserActivityLog$,
} from './effects/user-activity-log.effects';
import { manualEventsFeature } from './state/manual-events/manual-events.feature';
import { polygonsFeature } from './state/polygons/polygons.feature';
import { loadPolygons$ } from './effects/polygons.effects';
import {
  createManualEvent$,
  deleteManualEvent$,
  loadManualEvents$,
  loadMoreManualEvents$,
  reorderManualEvents$,
  startCreateManualEvent$,
  startDeleteManualEvent$,
  startLoadingManualEvents$,
  startUpdateManualEvent$,
  toggleManualEventActive$,
  updateManualEvent$,
} from './effects/manual-events.effects';

export const provideAppState = (): EnvironmentProviders[] => [
  provideStore({}),
  provideState(connectivityFeature),
  provideState(userFeature),
  provideState(userSettingsFeature),
  provideState(organizationFeature),
  provideState(permissionsFeature),
  provideState(routeFeature),
  provideState(sharedFeature),
  provideState(contactsFeature),
  provideState(contactGroupsFeature),
  provideState(orgUsersFeature),
  provideState(httpRequestFeature),
  provideState(systemConfigurationFeature),
  provideState(contactsActivityLogFeature),
  provideState(userActivityLogFeature),
  provideState(manualEventsFeature),
  provideState(polygonsFeature),
  provideEffects({ loadPolygons$ }),
  provideEffects({
    loadSystemConfiguration$,
    saveSystemConfiguration$,
  }),
  provideEffects({
    getMe,
    setInterfaceLanguage,
  }),
  provideEffects({
    loadActiveOrganization,
    loadMyOrganizations$,
  }),
  provideEffects({
    registerUserActivity$,
    inactivityTicks$,
    logoutCleanup$,
  }),
  provideEffects({
    startLoadRequiredData,
    getUserProfile,
    setStoreSession,
    armRedirectAfterLogin,
    redirectToHome,
    logout,
    logoutCallback,
    socialLoginByCode,
  }),
  provideEffects({
    getPermissions$,
  }),
  provideEffects({
    routerEvents$,
  }),
  provideEffects({
    showMessage$,
  }),
  provideEffects({
    startLoadingContacts$,
    loadContacts$,
    searchContacts$,
    resetContactsSearch$,
    reloadContacts$,
    loadMoreContacts$,
    loadMemberStats$,
    startImportContacts$,
    importContacts$,
    startCreateContact$,
    createContact$,
    startUpdateContact$,
    updateContact$,
    startDeleteContact$,
    deleteContact$,
  }),
  provideEffects({
    startLoadingContactGroups$,
    loadContactGroups$,
    startLoadingContactGroupMembers$,
    loadContactGroupMembers$,
    startCreateContactGroup$,
    createContactGroup$,
    startUpdateContactGroup$,
    updateContactGroup$,
    startUpdateContactGroupMembers$,
    updateContactGroupMembers$,
    startRemoveContactGroupMember$,
    removeContactGroupMember$,
    startDeleteContactGroup$,
    deleteContactGroup$,
  }),
  provideEffects({
    startLoadingOrgUsers$,
    loadOrgUsers$,
  }),
  provideEffects({
    startLoadingContactsActivityLog$,
    loadContactsActivityLog$,
    loadContactsActivityLogById$,
  }),
  provideEffects({
    startLoadingUserActivityLog$,
    loadUserActivityLog$,
  }),
  provideEffects({
    startLoadingManualEvents$,
    loadManualEvents$,
    loadMoreManualEvents$,
    startCreateManualEvent$,
    createManualEvent$,
    startUpdateManualEvent$,
    updateManualEvent$,
    toggleManualEventActive$,
    reorderManualEvents$,
    startDeleteManualEvent$,
    deleteManualEvent$,
  }),
  provideEffects({
    startLoadingHttpRequests$,
    loadHttpRequests$,
    startCreateHttpRequest$,
    createHttpRequest$,
    startUpdateHttpRequest$,
    updateHttpRequest$,
    startDeleteHttpRequest$,
    deleteHttpRequest$,
    startSetEnabled$,
    setEnabled$,
  }),
  provideRouterStore({
    stateKey: 'router',
    routerState: RouterState.Minimal,
  }),
  ...(isDevMode()
    ? [
        provideStoreDevtools({
          maxAge: 25,
          logOnly: false,
          autoPause: true,
          trace: false,
        }),
      ]
    : []),
];
